import { getLocalTimeZone, now } from "@internationalized/date"
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { StripeElementsOptions } from "@stripe/stripe-js"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Separator from "@/globalComponents/atoms/Separator"
import {
  ExpectedError,
  GatedPaymentIntentError,
} from "@/utilities/expectedErrors"
import { STRIPE_PROMISE } from "@/utilities/globals"
import {
  checkoutQueryOptions,
  confirmPaymentIntentQueryOptions,
  paymentIntentQueryOptions,
} from "@/utilities/hooks/queries/payment"
import { prettyCurrencyNumber } from "@/utilities/normalizers"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/$pamID/$sessionID/checkout"
)({
  beforeLoad: async ({ context, params }) => {
    const rsp = await context.queryClient.fetchQuery(
      paymentIntentQueryOptions({
        sessionID: params.sessionID,
        pamID: params.pamID,
      })
    )
    if (!rsp.requiresPayment) {
      const payRsp = await context.queryClient.fetchQuery(
        confirmPaymentIntentQueryOptions({
          stripePaymentIntentID: null,
          pamID: params.pamID,
        })
      )

      throw redirect({
        to: "/receipt/$receiptID",
        params: { receiptID: payRsp.receiptID },
      })
    }

    return { paymentIntent: rsp }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID, pamID, sessionID } = Route.useParams()
  const { paymentIntent } = Route.useRouteContext()
  const { t } = useTranslation()

  const { data: checkout } = useSuspenseQuery(
    checkoutQueryOptions({ sessionID })
  )

  const options: StripeElementsOptions = {
    /*
      We will always have a clientSecret here since if we don't
      that means it doesn't require payment and we already redirect
      them to the receipt page
    */
    clientSecret: paymentIntent.clientSecret!,
    customerSessionClientSecret:
      paymentIntent.customerSessionClientSecret || undefined,
    appearance: {
      variables: {
        fontFamily: "Inter",
      },
    },
    fonts: [
      {
        cssSrc:
          "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
      },
    ],
  }

  const baseUrl = window.location.origin
  const returnURL = `${baseUrl}/qr/${qrLookupID}/gated/${pamID}/${sessionID}/payment-status`

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("inlane.checkout.header")}
      </Heading>

      <Elements stripe={STRIPE_PROMISE} options={options}>
        <Card>
          <Heading level={1} as={2}>
            {t("inlane.checkout.subhead")}
          </Heading>
          <CheckoutForm
            sessionID={sessionID}
            pamID={pamID}
            returnURL={returnURL}
            amountDue={checkout.transactionDetails.amountDue}
            goBack={() =>
              navigate({
                to: "/qr/$qrLookupID/gated/$pamID/$sessionID/cart",
                replace: true,
              })
            }
            goNext={(receiptID: string) =>
              navigate({
                to: "/receipt/$receiptID",
                params: { receiptID },
              })
            }
          />
        </Card>
      </Elements>
    </>
  )
}

interface CheckoutFormProps {
  goBack: () => void
  goNext: (receiptID: string) => void
  returnURL: string
  amountDue: number
  pamID: string
  sessionID: string
}

function CheckoutForm(props: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <PaymentElement allow-top-navigation />
        <Separator
          orientation="horizontal"
          style={{ marginTop: "var(--space-5)" }}
        />
        <dl>
          <div style={{ marginTop: "var(--space-5)" }}>
            <dt>
              <Heading level={4} as={3}>
                {t("inlane.checkout.due")}
              </Heading>
            </dt>
            <dd>
              <p className="headline--4">
                {prettyCurrencyNumber(props.amountDue)}
              </p>
            </dd>
          </div>
        </dl>
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey)
              : error.message}
          </FieldError>
        )}
        <div data-actions-arrangement="horizontal">
          <Button hue="neutral" variant="default" onPress={props.goBack}>
            <Icon name="arrowLeft" slot="left" />
            {t("common.back")}
          </Button>
          <Button
            type="submit"
            hue="secondary"
            variant="default"
            isPending={loading}
          >
            {t("common.submit")}
            <Icon name="arrowRight" slot="right" />
          </Button>
        </div>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    if (!stripe || !elements) {
      setLoading(false)
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error("Payment details not found. Please refresh the page."))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: props.returnURL,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      } else {
        const rsp = await api.pay.POST({
          stripePaymentIntentID: result.paymentIntent.id,
          termsAndConditionsOK: now(getLocalTimeZone()).toAbsoluteString(),
          pamID: props.pamID,
        })
        if ("failReason" in rsp) {
          throw new GatedPaymentIntentError(rsp.failReason)
        }
        props.goNext(rsp.receiptID)
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("errors.common.somethingWentWrong")))
      }
    } finally {
      setLoading(false)
    }
  }
}
